
import Dexie, { Table } from 'dexie';

// Syncable interfaces with offline metadata
export interface SyncableUserProduct {
  id: string;
  user_id: string;
  name: string;
  normalized_name?: string;
  category_id?: string;
  default_quantity: number;
  default_unit: string;
  photo?: string;
  barcode?: string;
  openfoodfacts_id?: string;
  attributes: any[];
  created_at: string;
  updated_at: string;
  _syncStatus: 'pending' | 'synced' | 'deleted';
  _lastModified: number;
}

export interface SyncableUserCategory {
  id: string;
  user_id: string;
  name?: string;
  color?: string;
  icon?: string;
  text_color?: string;
  position?: number;
  visibile: boolean;
  created_at: string;
  updated_at: string;
  _syncStatus: 'pending' | 'synced' | 'deleted';
  _lastModified: number;
}

export interface SyncableShoppingList {
  id: string;
  name: string;
  user_id: string;
  is_primary: boolean;
  rapid_insert: boolean;
  shop_id?: string;
  background_gradient?: string;
  created_at: string;
  updated_at: string;
  _syncStatus: 'pending' | 'synced' | 'deleted';
  _lastModified: number;
}

export interface SyncableShoppingItem {
  id: string;
  list_id: string;
  product_id: string;
  quantity: string;
  unit?: string;
  category?: string;
  checked: boolean;
  position?: number;
  attributes: any[];
  created_at: string;
  user_id: string;
  _syncStatus: 'pending' | 'synced' | 'deleted';
  _lastModified: number;
}

// ✅ FIX: Allineato SyncableShop con il tipo Shop del database
export interface SyncableShop {
  id: string;
  shop_name: string; // ✅ Corretto: shop_name invece di name
  address?: string;
  shop_logo?: string; // ✅ Corretto: shop_logo invece di logo
  latitude?: number;
  longitude?: number;
  user_id: string;
  created_at: string;
  updated_at: string;
  _syncStatus: 'pending' | 'synced' | 'error' | 'deleted';
  _lastModified: number;
}

export interface SyncableCategoryShopOrdering {
  id?: string;
  shop_id: string;
  category_id: string;
  position: number;
  user_id: string;
  visibile: boolean;
  _syncStatus: 'pending' | 'synced' | 'deleted';
  _lastModified: number;
}

export interface SyncableRecurringProduct {
  id: string;
  user_id: string;
  product_id: string;
  frequency_days: number;
  last_purchase_date: string | null;
  next_purchase_date: string | null;
  active: boolean;
  attributes: any[];
  recurrence_group: string | null;
  created_at: string;
  updated_at: string;
  _syncStatus: 'pending' | 'synced' | 'deleted';
  _lastModified: number;
  
  // ✅ FIX: Aggiunti campi per dati prodotto salvati localmente per uso offline
  product_name?: string;
  product_default_quantity?: number;
  product_default_unit?: string;
  category_name?: string;
  category_icon?: string;
}

export interface SyncablePurchaseHistory {
  id: string;
  user_id: string;
  product_id: string;
  purchased_at: string;
  quantity?: number;
  price?: number;
  _syncStatus: 'pending' | 'synced' | 'deleted';
  _lastModified: number;
}

// ✅ FIX: Aggiunto purchased_by, category_id e product_attributes che mancavano
export interface SyncableShoppingHistory {
  id: string;
  user_id: string;
  product_id?: string;
  list_id?: string;
  product_name: string;
  quantity: number;
  unit: string;
  category_name?: string;
  category_id?: string; // ✅ Aggiunto campo mancante
  purchased_at: string;
  list_name: string;
  price?: number;
  purchased_by: string; // ✅ Aggiunto campo mancante
  product_attributes?: string; // ✅ Aggiunto campo mancante
  _syncStatus: 'pending' | 'synced' | 'error' | 'deleted';
  _lastModified: number;
}

export interface MostPurchasedCacheItem {
  product_id: string;
  product_name: string;
  count: number;
  category_id?: string;
  category_name?: string;
  cached_at: number;
}

export interface CachedImage {
  id: string;
  url: string;
  blob: Blob;
  cached_at: number;
  expires_at: number;
}

export interface LogEntry {
  id?: number;
  timestamp: number;
  level: 'log' | 'warn' | 'error';
  component: string;
  message: string;
  data?: any;
  action?: string;
  detail?: string;
}

export interface SyncableTutorialPreference {
  user_id: string;
  preferences: {
    globalEnabled: boolean;
    seenTutorials: Record<string, boolean>;
  };
  _syncStatus: 'pending' | 'synced' | 'deleted';
  _lastModified: number;
}

// ✅ FIX: Rinominato SyncQueueItem in SyncOperation per consistenza
export interface SyncOperation {
  id?: number;
  entityType: string;
  operation: 'create' | 'update' | 'delete';
  operation_type: 'create' | 'update' | 'delete';
  entityId: string;
  data?: any;
  timestamp: number;
  retryCount: number;
  /** Last error message for debugging failed operations */
  errorDetail?: string;
  status: 'pending' | 'completed' | 'failed';
}

// ✅ FIX: Alias per backward compatibility
export type SyncQueueItem = SyncOperation;

class OfflineDatabase extends Dexie {
  products!: Table<SyncableUserProduct>;
  categories!: Table<SyncableUserCategory>;
  shoppingLists!: Table<SyncableShoppingList>;
  shoppingItems!: Table<SyncableShoppingItem>;
  shops!: Table<SyncableShop, string>;
  categoryShopOrdering!: Table<SyncableCategoryShopOrdering, string>;
  recurringProducts!: Table<SyncableRecurringProduct>;
  purchaseHistory!: Table<SyncablePurchaseHistory>;
  syncQueue!: Table<SyncOperation>;
  logs!: Table<LogEntry>;
  shoppingHistory!: Table<SyncableShoppingHistory, string>;
  mostPurchasedCache!: Table<MostPurchasedCacheItem, string>;
  imageCache!: Table<CachedImage, string>;
  tutorialPreferences!: Table<SyncableTutorialPreference, string>;

  constructor() {
    super("CeraliaOfflineDB");

    this.version(13).stores({
      products: 'id, user_id, name, normalized_name, category_id, _syncStatus, _lastModified',
      categories: 'id, user_id, name, position, _syncStatus, _lastModified',
      shoppingLists: 'id, user_id, name, is_primary, _syncStatus, _lastModified',
      shoppingItems: 'id, list_id, product_id, category, checked, position, user_id, _syncStatus, _lastModified',
      shops: 'id, shop_name, user_id, _syncStatus, _lastModified', // ✅ Corretto: shop_name
      recurringProducts: 'id, user_id, product_id, active, recurrence_group, _syncStatus, _lastModified',
      purchaseHistory: 'id, user_id, product_id, purchased_at, _syncStatus, _lastModified',
      syncQueue: '++id, entityType, entityId, status, timestamp',
      logs: '++id, timestamp, level, component',
      shoppingHistory: 'id, user_id, product_id, list_id, purchased_at, _syncStatus, _lastModified',
      mostPurchasedCache: 'product_id, count, cached_at',
      imageCache: 'id, expires_at',
      categoryShopOrdering: 'id, shop_id, category_id, position, user_id, visibile, _syncStatus, _lastModified',
      tutorialPreferences: 'user_id, _syncStatus, _lastModified'
    }).upgrade(tx => {
      return tx.table('products').toCollection().modify(product => {
        if (typeof product.name === 'string') {
          product.normalized_name = product.name.trim().toLowerCase();
        }
      });
    });
  }
}

export const offlineDb = new OfflineDatabase();

export async function clearAllTables() {
  for (const key of Object.keys(offlineDb)) {
    const table: any = (offlineDb as any)[key];
    if (table && typeof table.clear === 'function') {
      await table.clear();
    }
  }
}
